/**
 * Unified hook for phone enrichment - combines status tracking and eligibility.
 *
 * One hook to rule them all: works for single contacts (PropertiesTab) and
 * batch contacts (table, bulk modal) with a single consistent query.
 *
 * Eligibility is computed locally from (contact properties + enrichment status):
 * 1. Doesn't already have a phone number
 * 2. No pending enrichment in progress
 * 3. Has LinkedIn URL (required for enrichment)
 * 4. Doesn't already have a completed/failed phone enrichment
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { getCostPerContact } from '@/lib/billing/creditCost';
import { useCreditBalance } from '@/lib/billing/useCreditBalance';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import { type CrmObject, getPropertyValue } from '@/types/crm';

export type EnrichmentStatus = 'idle' | 'pending' | 'completed' | 'failed';

export interface PhoneEnrichmentInfo {
  /** Current enrichment status for this contact */
  status: EnrichmentStatus;
  /** Whether contact is eligible for phone enrichment */
  isEligible: boolean;
  /** Reason why enrichment is disabled (if not eligible) */
  disabledReason: string | undefined;
  /** Whether contact already has a phone number */
  hasPhoneNumber: boolean;
  /** Whether contact has a LinkedIn URL */
  hasLinkedIn: boolean;
  /** Whether contact has any phone enrichment request (regardless of status) */
  hasPhoneEnrichment: boolean;
}

export interface UsePhoneEnrichmentResult {
  // Batch usage
  /** Map of contact entity_id to enrichment info */
  infoMap: Map<string, PhoneEnrichmentInfo>;
  /** Whether the hook is loading enrichment data */
  isLoading: boolean;
  /** Whether the hook encountered an error fetching enrichment data */
  isError: boolean;
  /** Trigger enrichment for a specific contact */
  triggerEnrichment: (contactEntityId: string) => Promise<void>;
  /** Map of contact entity_id to whether enrichment is being triggered */
  triggeringMap: Map<string, boolean>;
  /** Map of contact entity_id to error message (if any) */
  errorMap: Map<string, string | null>;

  // Single-contact convenience (first contact in array)
  /** Enrichment info for the first contact (convenience for single-contact usage) */
  info: PhoneEnrichmentInfo | undefined;
  /** Whether enrichment is being triggered for the first contact */
  isTriggering: boolean;
  /** Error message for the first contact */
  errorMessage: string | null;

  // Credit balance fields (gated by enforcement_enabled from usage API)
  /** True when credit enforcement is enabled for the current org */
  creditBalanceEnabled: boolean;
  /** Credits charged per phone enrichment (from API costs.phone, default 10) */
  creditCostPerContact: number;
  /** Total credits available (undefined when flag off or not loaded) */
  availableCredits: number | undefined;
}

interface EnrichmentRequest {
  id: string;
  entity_id: string;
  property_values: {
    ahoy_enrichment_type?: string;
    ahoy_status?: string;
    ahoy_created_at?: string;
  };
}

interface BatchObjectsResponse {
  objects: Record<string, EnrichmentRequest>;
}

/**
 * Unified hook for phone enrichment status and eligibility.
 *
 * @param orgId - Organization ID
 * @param contacts - Single contact or array of contacts
 * @returns Enrichment info, trigger function, and state
 *
 * @example Single contact (PropertiesTab)
 * ```tsx
 * const { info, triggerEnrichment, isTriggering, errorMessage } = usePhoneEnrichment(orgId, contact);
 * if (info?.isEligible) {
 *   <Button onClick={() => triggerEnrichment(contact.entity_id)}>Enrich</Button>
 * }
 * ```
 *
 * @example Multiple contacts (table)
 * ```tsx
 * const { infoMap, triggerEnrichment, triggeringMap } = usePhoneEnrichment(orgId, contacts);
 * const info = infoMap.get(contact.entity_id);
 * if (info?.status === 'pending') {
 *   // Show spinner
 * }
 * ```
 */
export function usePhoneEnrichment(
  orgId: number | undefined,
  contacts: CrmObject | CrmObject[] | undefined
): UsePhoneEnrichmentResult {
  const { user } = useAuthInfo();

  const { data: creditBalance } = useCreditBalance(orgId);
  const creditBalanceEnabled = creditBalance?.enforcement_enabled ?? false;
  const creditCostPerContact = getCostPerContact(creditBalance?.costs, 'phone');
  const availableCredits = creditBalance?.available;

  // Normalize to array
  const contactsArray = useMemo(() => {
    if (!contacts) {
      return [];
    }
    return Array.isArray(contacts) ? contacts : [contacts];
  }, [contacts]);

  // Track triggering state per contact
  const [triggeringMap, setTriggeringMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const [errorMap, setErrorMap] = useState<Map<string, string | null>>(
    new Map()
  );

  // Extract enrichment request IDs from all contacts' associations
  const enrichmentRequestIds = useMemo(() => {
    const ids: string[] = [];
    for (const contact of contactsArray) {
      for (const assoc of contact.associations || []) {
        if (
          assoc.association_type_name ===
            'ahoy_contact_to_enrichment_request' &&
          assoc.target_entity_id
        ) {
          ids.push(assoc.target_entity_id);
        }
      }
    }
    return [...new Set(ids)]; // Dedupe
  }, [contactsArray]);

  // Extract contact entity IDs for stable query key
  const contactEntityIds = useMemo(
    () => contactsArray.map(c => c.entity_id).sort(),
    [contactsArray]
  );

  // ONE batch fetch for all enrichment data
  const {
    data: enrichmentRequests,
    isLoading,
    isError,
  } = useQuery({
    // Query key includes enrichment request IDs as individual elements so
    // invalidateRelatedQueries (which checks key.includes(entityId)) can
    // match when an enrichment request entity gets a WS property update.
    // Previously IDs were comma-joined into a single string, which
    // Array.includes() couldn't match against individual entity IDs.
    queryKey: [
      'phone-enrichment',
      orgId,
      contactEntityIds.join(','),
      ...enrichmentRequestIds,
    ],
    queryFn: async () => {
      if (enrichmentRequestIds.length === 0) {
        return {};
      }
      const response = await apiPost<BatchObjectsResponse>(
        `/api/orgs/${orgId}/crm-events/crm-objects/by-entity-ids/`,
        // by-entity-ids now defaults include_associations to FALSE. This hook
        // reads only properties off the fetched requests (the association walk
        // above is over the CONTACT's own associations), so this passes false
        // explicitly to guard against a future default flip.
        { entity_ids: enrichmentRequestIds, include_associations: false }
      );
      return response.objects;
    },
    enabled: !!orgId && contactsArray.length > 0,
    staleTime: 30 * 1000, // 30 seconds - global polling handles cache refresh
    retry: 2,
  });

  // Compute info map (status + eligibility) from contacts + enrichment data
  const infoMap = useMemo(() => {
    const map = new Map<string, PhoneEnrichmentInfo>();
    const objectsMap = enrichmentRequests || {};

    for (const contact of contactsArray) {
      const hasPhoneNumber = !!getPropertyValue(
        contact.properties?.ahoy_phone_number
      );
      const hasLinkedIn = !!getPropertyValue(
        contact.properties?.ahoy_linkedin_url
      );

      // Find phone enrichment requests and determine status
      let hasPhoneEnrichment = false;
      // Start with idle - actual status comes from the enrichment request data
      let status: EnrichmentStatus = 'idle';

      // Collect all phone enrichment requests to find the most relevant status
      // Priority: pending > failed > completed > idle
      for (const assoc of contact.associations || []) {
        if (
          assoc.association_type_name === 'ahoy_contact_to_enrichment_request'
        ) {
          const req = objectsMap[assoc.target_entity_id];
          if (req?.property_values?.ahoy_enrichment_type === 'phone') {
            hasPhoneEnrichment = true;
            const reqStatus = req.property_values?.ahoy_status;
            if (reqStatus === 'queued' || reqStatus === 'pending') {
              // Pending takes highest priority - stop looking
              status = 'pending';
              break;
            } else if (reqStatus === 'failed') {
              // Upgrade to failed (if pending, we would have broken out of loop)
              if (status !== 'completed') {
                status = 'failed';
              }
            } else if (reqStatus === 'complete') {
              // Only upgrade to completed if still idle
              if (status === 'idle') {
                status = 'completed';
              }
            }
            // Don't break - continue looking for pending requests
          }
        }
      }

      // Override with local triggering state (optimistic UI)
      if (triggeringMap.get(contact.entity_id)) {
        status = 'pending';
      }

      // Compute eligibility locally (same logic as before)
      const isPending = status === 'pending';
      const isEligible =
        !hasPhoneNumber && !isPending && hasLinkedIn && !hasPhoneEnrichment;

      // Disabled reason with priority order
      let disabledReason: string | undefined;
      if (hasPhoneNumber) {
        disabledReason = 'Already has phone number';
      } else if (isPending) {
        disabledReason = 'Enrichment in progress';
      } else if (!hasLinkedIn) {
        disabledReason = 'LinkedIn URL required';
      } else if (hasPhoneEnrichment) {
        disabledReason = 'Already attempted phone enrichment';
      }

      map.set(contact.entity_id, {
        status,
        isEligible,
        disabledReason,
        hasPhoneNumber,
        hasLinkedIn,
        hasPhoneEnrichment,
      });
    }
    return map;
  }, [contactsArray, enrichmentRequests, triggeringMap]);

  // Trigger enrichment for a contact
  const triggerEnrichment = useCallback(
    async (contactEntityId: string) => {
      if (!orgId || !user?.id) {
        return;
      }

      setTriggeringMap(prev => new Map(prev).set(contactEntityId, true));
      setErrorMap(prev => {
        const next = new Map(prev);
        next.delete(contactEntityId);
        return next;
      });

      try {
        const entityId = crypto.randomUUID();

        await submitEventsOptimistic({
          orgId,
          userId: user.id,
          entityTypeName: 'ahoy_enrichment_request',
          primaryEntityId: entityId,
          errorToast: false,
          build: b =>
            b
              .objectCreated(entityId, 'ahoy_enrichment_request', {
                ahoy_enrichment_type: 'phone',
              })
              .associationAdded(
                entityId,
                'ahoy_enrichment_request',
                'ahoy_enrichment_request_to_contact',
                contactEntityId
              ),
        });

        // No manual invalidation needed - global CRM event polling handles cache refresh
        // The triggeringMap provides immediate optimistic UI feedback
      } catch (error) {
        logger.error(
          'Phone enrichment request failed',
          error instanceof Error ? error : undefined,
          { component: 'crm_enrichment', entity_id: contactEntityId }
        );
        const msg = "We couldn't start enrichment for this contact. Try again.";
        setErrorMap(prev => new Map(prev).set(contactEntityId, msg));
        toast.error('Enrichment failed', { description: msg });
      }
    },
    [orgId, user]
  );

  // Single-contact convenience accessors
  const firstContactId = contactsArray[0]?.entity_id;

  return {
    // Batch usage
    infoMap,
    isLoading,
    isError,
    triggerEnrichment,
    triggeringMap,
    errorMap,

    // Single-contact convenience
    info: firstContactId ? infoMap.get(firstContactId) : undefined,
    isTriggering: firstContactId
      ? (triggeringMap.get(firstContactId) ?? false)
      : false,
    errorMessage: firstContactId
      ? (errorMap.get(firstContactId) ?? null)
      : null,

    // Credit balance fields
    creditBalanceEnabled,
    creditCostPerContact,
    availableCredits,
  };
}

/**
 * List of contact properties that can be enriched.
 * These properties will show the EnrichIcon when empty.
 */
export const ENRICHABLE_PROPERTIES = new Set(['ahoy_phone_number']);

/**
 * Check if a property is enrichable.
 */
export function isEnrichableProperty(propertyName: string): boolean {
  return ENRICHABLE_PROPERTIES.has(propertyName);
}

/**
 * Check if a property value is empty (null, undefined, or empty string).
 */
export function isPropertyEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }
  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return true;
  }
  return false;
}
